import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { usePrefersReducedMotion, observeReveal } from '@hooks';
import { ProjectGrid, StyledProject, ProjectCardInner } from '@components/projectcard';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
    margin-bottom: 2rem;
  }

  .archive-link {
    font-family: var(--font-sans);
    font-size: var(--fz-lg);
    &:after {
      bottom: 0.4em;
    }
  }

  .grid-wrapper {
    width: 100%;
    margin-top: 2.5rem;
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: var(--space-7) auto 0;
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              slug
              description
              github
              external
              instagram
              ios
              android
            }
          }
        }
      }

      allProjects: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/projects/" } }
      ) {
        totalCount
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const stop = [
      observeReveal(revealTitle.current),
      observeReveal(revealArchiveLink.current),
      // Each card a tenth of a second behind the one before it.
      ...revealProjects.current.map((el, i) => observeReveal(el, i * 100)),
    ];

    return () => stop.forEach(fn => fn && fn());
  }, [prefersReducedMotion]);

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.map(({ node }) => node.frontmatter);
  const projectsToShow = showMore ? projects : projects.slice(0, GRID_LIMIT);

  return (
    // No id here: the #projects anchor belongs to the Featured section above,
    // which this one continues.
    <StyledProjectsSection>
      <h2 ref={revealTitle}>Other Noteworthy Projects</h2>

      <Link className="inline-link archive-link" to="/projects" ref={revealArchiveLink}>
        View all projects ({data.allProjects.totalCount})
      </Link>

      <div className="grid-wrapper">
        <ProjectGrid>
          {prefersReducedMotion ? (
            projectsToShow.map(project => (
              <StyledProject key={project.slug}>
                <ProjectCardInner project={project} />
              </StyledProject>
            ))
          ) : (
            <TransitionGroup component={null}>
              {projectsToShow.map((project, i) => (
                <CSSTransition
                  key={project.slug}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}>
                  <StyledProject
                    ref={el => (revealProjects.current[i] = el)}
                    style={{
                      transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                    }}>
                    <ProjectCardInner project={project} />
                  </StyledProject>
                </CSSTransition>
              ))}
            </TransitionGroup>
          )}
        </ProjectGrid>
      </div>

      {projects.length > GRID_LIMIT && (
        <button className="more-button" onClick={() => setShowMore(!showMore)}>
          Show {showMore ? 'Less' : 'More'}
        </button>
      )}
    </StyledProjectsSection>
  );
};

export default Projects;
